<template>
  <div>
    <v-card>
      <v-data-table
        :headers="headers"
        :items="softwares"
        :search="search"
        :items-per-page="30"
        :custom-filter="filterItems"
        :footer-props="{
          itemsPerPageOptions: [15, 30, -1],
          itemsPerPageText: $t('table.itemsPerPageText')
        }"
        sort-by="launched_date"
        sort-desc
        class="table-cursor"
      >
        <template v-slot:top>
          <v-toolbar flat color="white">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              :label="$t('search')"
              single-line
              hide-details
            />
            <v-spacer />
            <v-dialog v-model="dialog" max-width="500px">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="primary"
                  dark
                  class="mb-2"
                  v-bind="attrs"
                  v-on="on"
                  to="/manage/edit/softwares/add"
                  ><v-icon class="mr-2">mdi-plus-thick</v-icon
                  >{{ $t("button.newItem") }}</v-btn
                >
              </template>
            </v-dialog>
          </v-toolbar>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn class="mr-2" color="primary" :to="`/manage/edit/softwares/edit/${item.id}`" text icon>
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-icon color="red" @click="deleteItem(item)">
            mdi-delete
          </v-icon>
        </template>
        <template v-slot:item.software_type="{ item }">
          {{ $t(`form.software_types.${item.software_type || 'other'}`) }}
        </template>
        <template v-slot:item.status="{ item }">
          {{ $t(`form.software_statuses.${item.status || 'active'}`) }}
        </template>
        <template v-slot:item.responsible="{ item }">
          {{ responsiblePersonName(item) }}
        </template>
      </v-data-table>
      <v-row>
        <v-col class="text-right">
          <VueJsonToCsv :json-data="enrichedSoftwares" csv-title="softwares">
            <v-btn text color="secondary" class="mr-2" :disabled="enrichedSoftwares.length < 1">
              <v-icon left>mdi-file-table-box</v-icon>
              <span class="d-none d-md-block">{{ $t('table.exportCSV') }}</span>
              <span class="d-block d-md-none">{{ $t('table.exportCSV_short') }}</span>
            </v-btn>
          </VueJsonToCsv>
        </v-col>
        <v-col>
          <VueJsonToExcel :data="enrichedSoftwares" name="softwares.xls">
            <v-btn text color="secondary" :disabled="enrichedSoftwares.length < 1">
              <v-icon left>mdi-microsoft-excel</v-icon>
              <span class="d-none d-md-block">{{ $t('table.exportExcel') }}</span>
              <span class="d-block d-md-none">{{ $t('table.exportExcel_short') }}</span>
            </v-btn>
          </VueJsonToExcel>
        </v-col>
      </v-row>
    </v-card>
  </div>
</template>

<script>
import axios from "axios";
import VueJsonToCsv from "vue-json-to-csv";
import VueJsonToExcel from "vue-json-excel";
import software_model from "@/mixins/model_utils/software_model";
import filterSearchMixin from "../../mixins/filterSearchMixin.js";
export default {
  mixins: [software_model, filterSearchMixin],
  components: { VueJsonToCsv, VueJsonToExcel },
  data() {
    return {
      dialog: false,
      search: "",
      people: [],
      softwares: [],
    };
  },
  computed: {
    headers() {
      return [
        {
          text: this.$t("table.name"),
          value: "name",
        },
        {
          text: this.$t("form.software_type"),
          value: "software_type"
        },
        {
          text: this.$t("form.software_status"),
          value: "status"
        },
        {
          text: this.$t("form.software_current_version"),
          value: "current_version"
        },
        {
          text: this.$t("table.responsible"),
          value: "responsible",
        },
        {
          text: this.$t("table.launched_date"),
          value: "launched_date"
        },
        {
          text: this.$t("actions"),
          value: "actions",
          width: "10%",
          align: "end",
          filterable: false,
          sortable: false
        }
      ];
    },
    enrichedSoftwares() {
      return this.softwares.filter(item => this.filterItems("", this.search, item)).map(software => {
        return {
          id: software.id,
          name: software.name,
          software_type: this.$t(`form.software_types.${software.software_type || "other"}`),
          status: this.$t(`form.software_statuses.${software.status || "active"}`),
          current_version: software.current_version,
          responsible: this.responsiblePersonName(software),
          launched_date: software.launched_date,
        };
      });
    },
  },
  created() {
    this.initialize();
  },

  methods: {
    formatPersonName(person) {
      if (!person || !person.preferred_name) {
        return "";
      }
      const given = person.preferred_name.given_name || "";
      const family = person.preferred_name.family_name || "";
      return `${given} ${family}`.trim();
    },
    personFullName(personReference) {
      if (!personReference) {
        return "";
      }
      const person = personReference.person || personReference;
      if (person.preferred_name) {
        return this.formatPersonName(person);
      }
      const personId = person.id !== undefined ? person.id : personReference;
      const match = this.people.find(item => String(item.id) === String(personId));
      return match ? this.formatPersonName(match) : "";
    },
    responsiblePersonName(item) {
      return this.personFullName(item && item.responsible_person) || "-";
    },
    researchersFullNames(item) {
      if (!item || !Array.isArray(item.researchers)) {
        return "";
      }
      const names = item.researchers
        .map(researcher => {
          if (!researcher) {
            return "";
          }
          if (typeof researcher === "object") {
            if (researcher.person) {
              return this.formatPersonName(researcher.person);
            }
            if (researcher.preferred_name) {
              return this.formatPersonName(researcher);
            }
            if (researcher.id !== undefined && researcher.id !== null) {
              const match = this.people.find(person => String(person.id) === String(researcher.id));
              return match ? this.formatPersonName(match) : "";
            }
          }
          const identifier = typeof researcher === "number" || typeof researcher === "string" ? researcher : null;
          if (identifier === null) {
            return "";
          }
          const match = this.people.find(person => String(person.id) === String(identifier));
          return match ? this.formatPersonName(match) : "";
        })
        .filter(name => name && name.trim() !== "");
      return names.join(", ");
    },
    async initialize() {
      try {
        const endpoint = this.$store.state.endpoint;
        const [softwareResponse, peopleResponse] = await Promise.all([
          axios.get(`${endpoint}/software`),
          axios.get(`${endpoint}/personName`)
        ]);
        this.softwares = Array.isArray(softwareResponse.data) ? softwareResponse.data : [];
        this.people = Array.isArray(peopleResponse.data) ? peopleResponse.data : [];
      } catch (error) {
        console.log(error);
        if (this.$store && typeof this.$store.commit === "function") {
          this.$store.commit("alert", {
            color: "error",
            text: this.$t("message.error.loadData")
          });
        }
      }
    },

    deleteItem(item) {
      if (confirm(this.$t('message.confirm.delete'))) {
        let path = this.$store.state.endpoint + "/software/" + item.id;
        axios
          .delete(path)
          .then(response => {
            const index = this.softwares.indexOf(item);
            this.softwares.splice(index, 1);
            this.$store.commit("alert", {
              color: "success",
              text: this.$t('message.correct.deleteItem')
            });
          })
          .catch(error => {
            console.log(error);
            this.$store.commit("alert", {
              color: "error",
              text: error.response
            });
          });
      }
    },
    filterItems(value, search, item) {
      const search_array = search
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .split(",")
        .map(x => x.trim());
      const normalizedSearch = search_array.filter(term => term !== "");
      if (!normalizedSearch.length) {
        return true;
      }
      const source = [
        this.researchersFullNames(item),
        item && item.name ? item.name : "",
        item && item.software_type ? item.software_type : "",
        item && item.status ? item.status : "",
        item && item.current_version ? item.current_version : "",
        item && item.launched_date ? item.launched_date : ""
      ]
        .join(" ")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
      return (
        typeof source === "string" &&
        normalizedSearch.every(function(searchTerm) {
          return source.indexOf(searchTerm) !== -1;
        })
      );
    },
  }
};
</script>
